import React, { useState, useEffect } from 'react';

interface PostSessionModalProps {
  isOpen: boolean;
  onClose: () => void;
  sessionText: string;
  initialFilename: string;
  onSave: (filename: string, text: string, format: string) => void;
  onCopy: (text: string) => void;
  theme: string;
  wordCountAtCompletion: number;
  sessionDurationMinutes: number;
  deletionCount: number;
  totalWordsSaved?: number; // <--- NEW PROP (Optional for now)
}

const PostSessionModal: React.FC<PostSessionModalProps> = ({
  isOpen,
  onClose,
  sessionText,
  initialFilename,
  onSave,
  onCopy,
  theme,
  wordCountAtCompletion,
  sessionDurationMinutes,
  deletionCount,
  totalWordsSaved = 0 // Default to 0 if not passed
}) => {
  const [filename, setFilename] = useState(initialFilename);
  const [format, setFormat] = useState<string>(() => localStorage.getItem('lastExportFormat') || 'txt');

  useEffect(() => {
    setFilename(initialFilename);
  }, [initialFilename, isOpen]);

  useEffect(() => {
    if (isOpen) {
      const last = localStorage.getItem('lastExportFormat');
      if (last) setFormat(last);
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const handleSave = () => {
    localStorage.setItem('lastExportFormat', format);
    onSave(filename, sessionText, format);
  };

  const handleCopyToClipboard = () => {
    onCopy(sessionText);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 animate-fade-in">
      <div className={`bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl w-full max-w-md transform transition-all ${theme}`}>
        
        {/* HEADER */}
        <div className="flex justify-between items-start mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Session Survived! 🎉</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              You just saved <span className="font-bold text-primary dark:text-green-400">{wordCountAtCompletion} words</span> from the void.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>


        <div className="mb-4">
          <label htmlFor="session-filename" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Save filename as:
          </label>
          <input
            type="text"
            id="session-filename"
            value={filename}
            onChange={(e) => setFilename(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-primary focus:border-primary dark:bg-gray-700 dark:text-gray-100"
          />
        </div>

        <div className="mb-4">
          <label htmlFor="export-format" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            File format:
          </label>
          <select
            id="export-format"
            value={format}
            onChange={(e) => setFormat(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-primary focus:border-primary dark:bg-gray-700 dark:text-gray-100"
          >
            <option value="txt">Plain text (.txt)</option>
            <option value="md">Markdown (.md)</option>
            <option value="docx">Word (.docx)</option>
            <option value="pdf">PDF (.pdf)</option>
          </select>
        </div>

        <div className="space-y-3">
          <button
            onClick={handleSave}
            className="w-full bg-primary hover:bg-primary-dark text-white font-semibold py-2 px-4 rounded-md shadow-md transition-colors"
          >
            Save and Download
          </button>
          <button
            onClick={handleCopyToClipboard}
            className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded-md shadow-md transition-colors"
          >
            Copy Text to Clipboard
          </button>
          
          <button
            onClick={onClose}
            className="w-full bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-100 font-semibold py-2 px-4 rounded-md shadow-md transition-colors"
          >
            Close
          </button>
        </div>

        {/* --- FINAL GOLDEN FOOTER --- */}
        <div className="mt-6 pt-5 border-t border-gray-200 dark:border-gray-700">
          {/* A subtle lead-in question to soften the ask */}
          <p className="text-center text-sm text-gray-500 dark:text-gray-400 mb-3">
            Did this session help you write?
          </p>
          
          {/* The Golden Button */}
          <a
            href="https://buymeacoffee.com/getdeleto"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full bg-[#FFDD00] hover:bg-[#FFEA00] text-black font-bold py-3 px-4 rounded-md shadow transition-colors flex items-center justify-center gap-2"
          >
            <span className="text-xl">☕</span>
            <span> Help fuel the next session</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default PostSessionModal;